import { useState } from 'react';
import type { RoleDescriptor, RoleIdPair } from './types';

export interface RoleAssignmentPanelProps {
  userId: string;
  assignableRoles: RoleDescriptor[];
  currentRoleIds: string[];
  /** Client-side mirror of DB hard-block list. Empty in V1 (only hard-block is
   *  external_auditor user_type, which is never offered in the UI assignableRoles). */
  hardBlockCombos?: RoleIdPair[];
  /** Warn combos — plumbed but empty in V1 per D-SK-3 restatement. */
  warnCombos?: RoleIdPair[];
  /** 'tenant' | 'platform-admin'. Platform-admin is wired but not consumed in V1. */
  mode: 'tenant' | 'platform-admin';
  /** Roles shown but not changeable by this actor (USER_ADMIN cluster: the privileged roles, for a user_admin) —
   *  rendered disabled with `lockedLabel`, kept as they are. The server refuses them regardless; this keeps the
   *  offered set equal to the set the server accepts. */
  lockedRoleIds?: string[];
  lockedLabel?: string;
  /** Called with the diff when Save is clicked. Throws on API error (message shown in banner). */
  onSave: (diff: { add: string[]; remove: string[] }) => Promise<{ roleIds: string[] }>;
  onSaveSuccess: (newRoleIds: string[]) => void;
}

function combosContain(combos: RoleIdPair[], selected: Set<string>): RoleIdPair | undefined {
  return combos.find(
    (c) => selected.has(c.roleAId) && selected.has(c.roleBId),
  );
}

export function RoleAssignmentPanel({
  assignableRoles,
  currentRoleIds,
  hardBlockCombos = [],
  warnCombos = [],
  lockedRoleIds = [],
  lockedLabel = 'Managed by admins',
  onSave,
  onSaveSuccess,
}: RoleAssignmentPanelProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(currentRoleIds));
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [warnCombo, setWarnCombo] = useState<RoleIdPair | undefined>(undefined);

  const sortedRoles = [...assignableRoles].sort((a, b) => a.name.localeCompare(b.name));

  function toggleRole(id: string) {
    if (lockedRoleIds.includes(id)) return;   // not this actor's to change — never only the disabled attribute
    setErrorMsg(null);
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);

    // Re-evaluate warn combo on every change
    const hit = combosContain(warnCombos, next);
    setWarnCombo(hit);
  }

  const hasZeroRoles = selected.size === 0;
  const hardBlockHit = combosContain(hardBlockCombos, selected);
  const saveDisabled = saving || hasZeroRoles || !!hardBlockHit;

  async function handleSave() {
    setErrorMsg(null);
    const currentSet = new Set(currentRoleIds);
    const add = [...selected].filter((id) => !currentSet.has(id));
    const remove = [...currentSet].filter((id) => !selected.has(id));

    if (add.length === 0 && remove.length === 0) {
      // No-op — nothing changed
      return;
    }

    setSaving(true);
    try {
      const result = await onSave({ add, remove });
      onSaveSuccess(result.roleIds);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to update roles. Please try again.';
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Hard-block error banner */}
      {(hardBlockHit || errorMsg) && (
        <div className="rounded-lg border border-accent-rose/40 bg-accent-rose/10 px-4 py-3 text-sm text-accent-rose">
          {hardBlockHit
            ? 'This role combination is not permitted. Please deselect one of the conflicting roles.'
            : errorMsg}
        </div>
      )}

      {/* Warn banner — plumbed but hidden in V1 (warnCombos default []) */}
      {warnCombo && !hardBlockHit && (
        <div className="rounded-lg border border-accent-amber/40 bg-accent-amber/10 px-4 py-3 text-sm text-accent-amber">
          This combination is unusual — confirm before saving.
        </div>
      )}

      {/* Zero-roles banner */}
      {hasZeroRoles && (
        <div className="rounded-lg border border-accent-rose/40 bg-accent-rose/10 px-4 py-3 text-sm text-accent-rose">
          User must have at least one role.
        </div>
      )}

      {/* Checkbox list */}
      <div className="flex flex-col gap-1">
        {sortedRoles.map((role) => {
          const checked = selected.has(role.id);
          const locked = lockedRoleIds.includes(role.id);
          return (
            <label
              key={role.id}
              className={`flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-bg-elevated'}`}
              title={locked ? lockedLabel : undefined}
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-accent-cyan disabled:cursor-not-allowed"
                checked={checked}
                onChange={() => toggleRole(role.id)}
                disabled={saving || locked}
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-text-primary">
                  {role.name}
                  {locked && <span className="ml-2 text-xs font-normal text-text-muted">{lockedLabel}</span>}
                </span>
                {role.description && (
                  <span className="text-xs text-text-secondary">{role.description}</span>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {/* Save button */}
      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={handleSave}
          disabled={saveDisabled}
          title={hasZeroRoles ? 'User must have at least one role' : undefined}
          className="rounded-lg bg-accent-cyan px-4 py-2 text-sm font-medium text-bg-base transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save roles'}
        </button>
      </div>
    </div>
  );
}
